import { createFileRoute } from '@tanstack/react-router';
import {
  Avatar,
  Button,
  Card,
  Divider,
  Group,
  PasswordInput,
  Select,
  Skeleton,
  Stack,
  Text,
  TextInput,
} from '@mantine/core';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { motion } from 'framer-motion';
import { notifications } from '@mantine/notifications';
import { trpc } from '../../lib/trpc';

const ProfileSchema = z.object({
  firstName: z.string().trim().max(80).optional(),
  lastName: z.string().trim().max(80).optional(),
  displayName: z.string().trim().max(160).optional(),
  preferredLanguage: z.enum(['en', 'hi']).optional(),
  timezone: z.string().trim().max(80).optional(),
  mobileNumber: z.string().trim().max(40).optional(),
});
type ProfileForm = z.infer<typeof ProfileSchema>;

const PasswordSchema = z
  .object({
    currentPassword: z.string().min(1),
    newPassword: z.string().min(8),
    newPasswordConfirmation: z.string().min(8),
  })
  .refine((d) => d.newPassword === d.newPasswordConfirmation, {
    message: 'Passwords do not match',
    path: ['newPasswordConfirmation'],
  });
type PasswordForm = z.infer<typeof PasswordSchema>;

function Profile() {
  const me = trpc.auth.me.useQuery();
  const updateProfile = trpc.auth.updateProfile.useMutation();
  const changePassword = trpc.auth.changePassword.useMutation();

  const profileForm = useForm<ProfileForm>({
    resolver: zodResolver(ProfileSchema),
    values: {
      firstName: me.data?.firstName ?? '',
      lastName: me.data?.lastName ?? '',
      displayName: me.data?.displayName ?? '',
      preferredLanguage: (me.data?.preferredLanguage as 'en' | 'hi') ?? 'en',
      timezone: me.data?.timezone ?? '',
      mobileNumber: '',
    },
  });

  const passwordForm = useForm<PasswordForm>({ resolver: zodResolver(PasswordSchema) });

  const onSaveProfile = profileForm.handleSubmit(async (values) => {
    try {
      await updateProfile.mutateAsync(values);
      notifications.show({ message: 'Profile updated', color: 'green' });
      void me.refetch();
    } catch (err) {
      notifications.show({ message: (err as Error).message, color: 'red' });
    }
  });

  const onChangePassword = passwordForm.handleSubmit(async (values) => {
    try {
      await changePassword.mutateAsync(values);
      notifications.show({
        message: 'Password changed. Please sign in again.',
        color: 'green',
      });
      window.location.href = '/auth/login';
    } catch (err) {
      notifications.show({ message: (err as Error).message, color: 'red' });
    }
  });

  if (me.isPending) return <Skeleton height={400} radius="md" />;
  if (me.isError || !me.data)
    return (
      <Card>
        <Text c="red">Could not load profile.</Text>
      </Card>
    );

  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
      <Stack gap="lg">
        <Card>
          <Stack gap="sm">
            <Group>
              <Avatar src={me.data.profilePictureUrl} alt={me.data.displayName ?? me.data.email} />
              <div>
                <Text fw={600}>{me.data.displayName ?? me.data.email}</Text>
                <Text c="dimmed" size="sm">
                  {me.data.email}
                </Text>
              </div>
            </Group>
            <Divider />
            <form onSubmit={onSaveProfile}>
              <Stack gap="sm">
                <Group grow>
                  <TextInput label="First name" {...profileForm.register('firstName')} />
                  <TextInput label="Last name" {...profileForm.register('lastName')} />
                </Group>
                <TextInput label="Display name" {...profileForm.register('displayName')} />
                <Group grow>
                  <Select
                    label="Language"
                    data={[
                      { value: 'en', label: 'English' },
                      { value: 'hi', label: 'हिन्दी' },
                    ]}
                    value={profileForm.watch('preferredLanguage') ?? 'en'}
                    onChange={(v) =>
                      profileForm.setValue('preferredLanguage', (v as 'en' | 'hi') ?? 'en')
                    }
                  />
                  <TextInput label="Timezone" {...profileForm.register('timezone')} />
                </Group>
                <TextInput label="Mobile number" {...profileForm.register('mobileNumber')} />
                <Button type="submit" loading={updateProfile.isPending}>
                  Save profile
                </Button>
              </Stack>
            </form>
          </Stack>
        </Card>

        <Card>
          <Stack gap="sm">
            <Text fw={600}>Change password</Text>
            <form onSubmit={onChangePassword}>
              <Stack gap="sm">
                <PasswordInput
                  label="Current password"
                  autoComplete="current-password"
                  {...passwordForm.register('currentPassword')}
                />
                <PasswordInput
                  label="New password"
                  autoComplete="new-password"
                  {...passwordForm.register('newPassword')}
                  error={passwordForm.formState.errors.newPassword?.message}
                />
                <PasswordInput
                  label="Confirm new password"
                  autoComplete="new-password"
                  {...passwordForm.register('newPasswordConfirmation')}
                  error={passwordForm.formState.errors.newPasswordConfirmation?.message}
                />
                <Button type="submit" loading={changePassword.isPending}>
                  Change password
                </Button>
              </Stack>
            </form>
          </Stack>
        </Card>
      </Stack>
    </motion.div>
  );
}

export const Route = createFileRoute('/auth/profile')({ component: Profile });
